import type { CustomerAtRisk } from "@pilatestime/shared-types";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	type ColumnDef,
	flexRender,
	getCoreRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchOrgMgmtCustomerRiskAnalysisQueryOptions } from "@/api/orgMgmtCustomerRiskAnalysis";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useApiParameters } from "@/hooks/useApiParameters";
import { OrganizationManagementNavigation } from "@/routes/_auth/app/_app/org/$orgId/org-management/-components/OrganizationManagementNavigation";

const DEFAULT_WINDOW_DAYS = 30;
const DEFAULT_MIN_CANCELLATIONS = 2;
const WINDOW_DAYS_OPTIONS = [7, 14, 30, 60, 90];

type RiskLevelKey = "veryLow" | "low" | "medium" | "high" | "veryHigh";

function getRiskLevel(attendanceRate: number): {
	labelKey: RiskLevelKey;
	badgeClass: string;
} {
	if (attendanceRate >= 90) {
		return { labelKey: "veryLow", badgeClass: "badge badge-success" };
	}
	if (attendanceRate >= 80) {
		return {
			labelKey: "low",
			badgeClass: "badge badge-success badge-outline",
		};
	}
	if (attendanceRate >= 70) {
		return { labelKey: "medium", badgeClass: "badge badge-warning" };
	}
	if (attendanceRate > 60) {
		return {
			labelKey: "high",
			badgeClass: "badge badge-error badge-outline",
		};
	}
	return { labelKey: "veryHigh", badgeClass: "badge badge-error" };
}

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/org-management/customer-risk-analysis/",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		context.queryClient.ensureQueryData(
			fetchOrgMgmtCustomerRiskAnalysisQueryOptions(
				orgId,
				token,
				DEFAULT_WINDOW_DAYS,
				DEFAULT_MIN_CANCELLATIONS,
			),
		);

		return { userId, token, orgId };
	},
	component: CustomerRiskAnalysisPage,
});

function CustomerRiskAnalysisPage() {
	const { t } = useTranslation();
	const { token, orgId } = Route.useLoaderData();

	const [windowDays, setWindowDays] = useState(DEFAULT_WINDOW_DAYS);
	const [minCancellations, setMinCancellations] = useState(
		DEFAULT_MIN_CANCELLATIONS,
	);

	const { data, isLoading, isError } = useQuery(
		fetchOrgMgmtCustomerRiskAnalysisQueryOptions(
			orgId,
			token,
			windowDays,
			minCancellations,
		),
	);

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("orgManagement.customerRiskAnalysis.title")}
				description={t("orgManagement.customerRiskAnalysis.subtitle")}
				showBackButton={true}
				backButtonText={t("orgManagement.back")}
				hideOnMobile={false}
			/>

			<div
				className="flex-1 flex h-[calc(100vh-4rem)] min-h-0 overflow-hidden"
				data-testid="organization-management-customer-risk-container"
			>
				<OrganizationManagementNavigation activeTab="customer-risk-analysis" />

				<div
					className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto"
					data-testid="organization-management-customer-risk-main"
				>
					<div className="space-y-4 max-w-6xl mx-auto">
						<FilterControls
							windowDays={windowDays}
							onWindowDaysChange={setWindowDays}
							minCancellations={minCancellations}
							onMinCancellationsChange={setMinCancellations}
						/>

						<Card>
							<CardContent className="p-3 md:p-4">
								{isLoading && (
									<div className="flex items-center justify-center h-32 text-base-content/60 text-sm">
										{t("orgManagement.customerRiskAnalysis.loading")}
									</div>
								)}
								{isError && (
									<div className="flex items-center justify-center h-32 text-error text-sm">
										{t("orgManagement.customerRiskAnalysis.error")}
									</div>
								)}
								{!isLoading && !isError && data && (
									<CustomerRiskTable orgId={orgId} customers={data.customers} />
								)}
							</CardContent>
						</Card>
					</div>
				</div>
			</div>
		</div>
	);
}

interface FilterControlsProps {
	windowDays: number;
	onWindowDaysChange: (next: number) => void;
	minCancellations: number;
	onMinCancellationsChange: (next: number) => void;
}

function FilterControls({
	windowDays,
	onWindowDaysChange,
	minCancellations,
	onMinCancellationsChange,
}: FilterControlsProps) {
	const { t } = useTranslation();

	return (
		<Card>
			<CardContent className="p-3 md:p-4">
				<div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 items-end">
					<div>
						<Label className="text-xs text-base-content/60">
							{t("orgManagement.customerRiskAnalysis.window")}
						</Label>
						<Select
							value={String(windowDays)}
							onValueChange={(value) => onWindowDaysChange(Number(value))}
						>
							<SelectTrigger className="mt-1">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{WINDOW_DAYS_OPTIONS.map((days) => (
									<SelectItem key={days} value={String(days)}>
										{days}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div>
						<Label className="text-xs text-base-content/60">
							{t("orgManagement.customerRiskAnalysis.minCancellations")}
						</Label>
						<Input
							type="number"
							min={1}
							max={50}
							value={minCancellations}
							onChange={(e) => {
								const next = Number(e.target.value);
								if (!Number.isNaN(next) && next >= 1 && next <= 50) {
									onMinCancellationsChange(next);
								}
							}}
							className="mt-1"
						/>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

interface CustomerRiskTableProps {
	orgId: string;
	customers: CustomerAtRisk[];
}

function CustomerRiskTable({ orgId, customers }: CustomerRiskTableProps) {
	const { t } = useTranslation();

	const columns = useMemo<ColumnDef<CustomerAtRisk>[]>(
		() => [
			{
				accessorKey: "customerFullName",
				header: t("orgManagement.customerRiskAnalysis.columns.customer"),
				cell: ({ row }) => (
					<Link
						to="/app/org/$orgId/customers/$customerId"
						params={{ orgId, customerId: row.original.customerId }}
						className="text-primary hover:underline"
					>
						{row.original.customerFullName}
					</Link>
				),
			},
			{
				accessorKey: "customerPlanName",
				header: t("orgManagement.customerRiskAnalysis.columns.plan"),
			},
			{
				accessorKey: "cancellationCount",
				header: t("orgManagement.customerRiskAnalysis.columns.absences"),
				cell: ({ row }) => (
					<span className="badge badge-error tabular-nums">
						{row.original.cancellationCount}
					</span>
				),
			},
			{
				accessorKey: "scheduledCount",
				header: t("orgManagement.customerRiskAnalysis.columns.scheduled"),
				cell: ({ row }) => (
					<span className="tabular-nums">{row.original.scheduledCount}</span>
				),
			},
			{
				id: "attendanceRate",
				header: t("orgManagement.customerRiskAnalysis.columns.attendanceRate"),
				accessorFn: (row) =>
					row.scheduledCount > 0
						? (row.scheduledCount - row.cancellationCount) / row.scheduledCount
						: null,
				cell: ({ row }) => {
					const { scheduledCount, cancellationCount } = row.original;
					if (scheduledCount <= 0) {
						return <span className="text-base-content/60">—</span>;
					}
					const rate =
						((scheduledCount - cancellationCount) / scheduledCount) * 100;
					const badgeClass =
						rate >= 80
							? "badge badge-success"
							: rate >= 50
								? "badge badge-warning"
								: "badge badge-error";
					return (
						<span className={`${badgeClass} tabular-nums`}>
							{rate.toFixed(0)}%
						</span>
					);
				},
			},
			{
				id: "risk",
				header: t("orgManagement.customerRiskAnalysis.columns.risk"),
				cell: ({ row }) => {
					const { scheduledCount, cancellationCount } = row.original;
					if (scheduledCount <= 0) {
						return <span className="text-base-content/60">—</span>;
					}
					const rate =
						((scheduledCount - cancellationCount) / scheduledCount) * 100;
					const { labelKey, badgeClass } = getRiskLevel(rate);
					return (
						<span className={badgeClass}>
							{t(`orgManagement.customerRiskAnalysis.riskLevels.${labelKey}`)}
						</span>
					);
				},
			},
		],
		[t, orgId],
	);

	const table = useReactTable({
		data: customers,
		columns,
		getCoreRowModel: getCoreRowModel(),
	});

	if (customers.length === 0) {
		return (
			<div className="flex items-center justify-center h-32 text-base-content/60 text-sm">
				{t("orgManagement.customerRiskAnalysis.empty")}
			</div>
		);
	}

	return (
		<>
			<div className="md:hidden space-y-2">
				{customers.map((customer) => (
					<CustomerRiskMobileCard
						key={customer.customerId}
						orgId={orgId}
						customer={customer}
					/>
				))}
			</div>
			<div className="hidden md:block overflow-x-auto">
				<table className="table table-zebra w-full">
					<thead>
						{table.getHeaderGroups().map((headerGroup) => (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<th key={header.id} className="text-base-content/80">
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
									</th>
								))}
							</tr>
						))}
					</thead>
					<tbody>
						{table.getRowModel().rows.map((row) => (
							<tr key={row.id}>
								{row.getVisibleCells().map((cell) => (
									<td key={cell.id}>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</>
	);
}

interface CustomerRiskMobileCardProps {
	orgId: string;
	customer: CustomerAtRisk;
}

function CustomerRiskMobileCard({
	orgId,
	customer,
}: CustomerRiskMobileCardProps) {
	const { t } = useTranslation();
	const { scheduledCount, cancellationCount } = customer;
	const hasRate = scheduledCount > 0;
	const rate = hasRate
		? ((scheduledCount - cancellationCount) / scheduledCount) * 100
		: null;
	const risk = rate !== null ? getRiskLevel(rate) : null;
	const attendanceBadge =
		rate === null
			? "text-base-content/60"
			: rate >= 80
				? "badge badge-success"
				: rate >= 50
					? "badge badge-warning"
					: "badge badge-error";

	return (
		<div className="rounded-lg border border-base-300 bg-base-100 p-3 space-y-3">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0 flex-1">
					<Link
						to="/app/org/$orgId/customers/$customerId"
						params={{ orgId, customerId: customer.customerId }}
						className="text-primary hover:underline font-medium block truncate"
					>
						{customer.customerFullName}
					</Link>
					<p className="text-xs text-base-content/60 truncate">
						{customer.customerPlanName}
					</p>
				</div>
				{risk && (
					<span className={`${risk.badgeClass} shrink-0`}>
						{t(
							`orgManagement.customerRiskAnalysis.riskLevels.${risk.labelKey}`,
						)}
					</span>
				)}
			</div>
			<div className="grid grid-cols-3 gap-2 text-center">
				<MobileStat
					label={t("orgManagement.customerRiskAnalysis.columns.absences")}
					value={
						<span className="badge badge-error tabular-nums">
							{cancellationCount}
						</span>
					}
				/>
				<MobileStat
					label={t("orgManagement.customerRiskAnalysis.columns.scheduled")}
					value={<span className="tabular-nums">{scheduledCount}</span>}
				/>
				<MobileStat
					label={t("orgManagement.customerRiskAnalysis.columns.attendanceRate")}
					value={
						rate === null ? (
							<span className={attendanceBadge}>—</span>
						) : (
							<span className={`${attendanceBadge} tabular-nums`}>
								{rate.toFixed(0)}%
							</span>
						)
					}
				/>
			</div>
		</div>
	);
}

interface MobileStatProps {
	label: string;
	value: ReactNode;
}

function MobileStat({ label, value }: MobileStatProps) {
	return (
		<div className="flex flex-col items-center gap-1">
			<span className="text-[10px] uppercase tracking-wide text-base-content/60">
				{label}
			</span>
			<span className="text-sm">{value}</span>
		</div>
	);
}
